/**
 * Chart geometry and time ranges for the calls page.
 *
 * The donut is hand-rolled SVG rather than a charting library: the console is already a single
 * 315KB bundle, and a library would add more than this file for one chart. The arc maths is pure
 * and tested, which is the part worth getting right.
 */

export interface Slice {
  label: string;
  value: number;
  /** CSS colour. */
  color: string;
}

export interface Arc extends Slice {
  /** SVG path for this segment of the ring. */
  path: string;
  /** Share of the whole, 0..1. */
  share: number;
}

const TAU = Math.PI * 2;

function pointOn(cx: number, cy: number, r: number, turns: number): [number, number] {
  // Start at 12 o'clock and go clockwise, which is how people read a pie.
  const angle = turns * TAU - TAU / 4;
  return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
}

/**
 * Ring segments for a donut chart.
 *
 * Zero-value slices are dropped rather than drawn as invisible arcs, so the legend only lists
 * outcomes that actually happened.
 */
export function donutArcs<S extends Slice>(
  slices: readonly S[],
  opts: { size?: number; thickness?: number } = {}
): (S & Arc)[] {
  const size = opts.size ?? 160;
  const thickness = opts.thickness ?? 28;
  const cx = size / 2;
  const cy = size / 2;
  const outer = size / 2;
  const inner = outer - thickness;

  const present = slices.filter((s) => s.value > 0);
  const total = present.reduce((n, s) => n + s.value, 0);
  if (total === 0) return [];

  // A single slice can't be drawn as an arc — the start and end points coincide, so the path
  // collapses. Two half-circles make a full ring instead.
  if (present.length === 1) {
    const only = present[0]!;
    const ring =
      `M ${cx} ${cy - outer} A ${outer} ${outer} 0 1 1 ${cx} ${cy + outer}` +
      ` A ${outer} ${outer} 0 1 1 ${cx} ${cy - outer} Z` +
      `M ${cx} ${cy - inner} A ${inner} ${inner} 0 1 0 ${cx} ${cy + inner}` +
      ` A ${inner} ${inner} 0 1 0 ${cx} ${cy - inner} Z`;
    return [{ ...only, path: ring, share: 1 }];
  }

  let cursor = 0;
  return present.map((s) => {
    const share = s.value / total;
    const start = cursor;
    const end = cursor + share;
    cursor = end;

    const [ox1, oy1] = pointOn(cx, cy, outer, start);
    const [ox2, oy2] = pointOn(cx, cy, outer, end);
    const [ix2, iy2] = pointOn(cx, cy, inner, end);
    const [ix1, iy1] = pointOn(cx, cy, inner, start);
    const large = share > 0.5 ? 1 : 0;

    return {
      ...s,
      share,
      path:
        `M ${ox1} ${oy1} A ${outer} ${outer} 0 ${large} 1 ${ox2} ${oy2}` +
        ` L ${ix2} ${iy2} A ${inner} ${inner} 0 ${large} 0 ${ix1} ${iy1} Z`
    };
  });
}

/** Whole percent for a legend. Rounded for display only, never for arithmetic. */
export function sharePercent(share: number): number {
  return Math.round(share * 100);
}

export interface RangePreset {
  key: string;
  label: string;
  /** Days back from now; 0 means "since midnight today". */
  days: number;
}

export const RANGE_PRESETS: RangePreset[] = [
  { key: "today", label: "Today", days: 0 },
  { key: "7d", label: "Last 7 days", days: 7 },
  { key: "30d", label: "Last 30 days", days: 30 },
  { key: "90d", label: "Last 90 days", days: 90 }
];

/** ISO bounds for a preset. `until` is now, so the range always includes the newest call. */
export function presetRange(days: number, now = new Date()): { since: string; until: string } {
  if (days === 0) {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    return { since: start.toISOString(), until: now.toISOString() };
  }
  return {
    since: new Date(now.getTime() - days * 86_400_000).toISOString(),
    until: now.toISOString()
  };
}

/** ISO bounds for two "YYYY-MM-DD" dates, inclusive of both local days. */
export function customRange(fromDate: string, toDate: string): { since: string; until: string } {
  const [fy, fm, fd] = fromDate.split("-").map(Number);
  const [ty, tm, td] = toDate.split("-").map(Number);
  return {
    since: new Date(fy!, (fm ?? 1) - 1, fd ?? 1, 0, 0, 0, 0).toISOString(),
    until: new Date(ty!, (tm ?? 1) - 1, td ?? 1, 23, 59, 59, 999).toISOString()
  };
}
